import React, { useState, useEffect, useMemo } from 'react';
import { XOctagon, ChevronDown, X, PhoneOff, AlertTriangle, MessageSquare, ShieldAlert, Layers, ExternalLink, Lock, Tag } from 'lucide-react';
import { Negociacao, Profile } from '../../../types';
import { supabase } from '../../../src/lib/supabase';
import AvalistaRecoveryPanel from '../components/NegotiationDetails/AvalistaRecoveryPanel';
import { negociacoesComDeclaracao } from '../../../src/services/declaracaoNaoAssociacao';
import { useMotivosReprovacao } from '../../../src/hooks/useMotivosReprovacao';
import { descreverMotivosReprovacao } from '../../../src/lib/motivosReprovacao';

// Colunas da análise que só este modal consome. Antes vinham no payload do board — para
// todas as negociações do funil — só para servir às poucas linhas reprovadas listadas
// aqui. Agora são buscadas ao abrir o modal, junto dos avalistas.
const ANALISE_COLUNAS_DETALHE =
    'negociacao_id, decisao_final, decisao_final_at, risco_final, ressalvas_condicoes, ' +
    'pendencias_obs_docs, parecer_credito, analista_id, credito_pendencias_financeiras, ' +
    'credito_valor_pendencias, credito_protestos, credito_valor_protestos, credito_cheques_fundo, ' +
    'credito_valor_cheques, credito_score_serasa, credito_classificacao_risco, ' +
    'verificacao_telefonica_resultado, verificacao_telefonica_status';
// Motivo da reprovação (#6428). Separado para poder buscar sem ele se a coluna
// ainda não existir no banco — senão a lista inteira de análises falharia.
const ANALISE_COLUNAS_MOTIVO = ', motivo_reprovacao, motivos_reprovacao_outros, motivo_reprovacao_retroativo';

interface TermosReprovadosModalProps {
    negociacoes: Negociacao[];
    profiles: Profile[];
    onClose: () => void;
    onOpenDetails: (negociacao: Negociacao) => void;
    onUpdate: () => void;
}

const VERIFICACAO_LABELS: Record<string, string> = {
    confirmado: 'O cliente confirmou a contratação na verificação telefônica.',
    nao_confirmou: 'O cliente não confirmou a contratação na verificação telefônica.',
    numero_errado: 'A verificação telefônica não foi concluída: número de contato errado.',
    sem_resposta: 'A verificação telefônica não foi concluída: cliente sem resposta.',
};

const formatDate = (value?: string) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatMoney = (value?: number | null) => {
    if (!value) return '';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Até 2 casas, igual ao card do Kanban: arredondar cada linha faria a coluna não somar o total.
const formatKwh = (value: number) => `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kWh`;
const consumoDe = (n: Negociacao) => Number(n.consumo_medio_kwh) || 0;

const parseVerificacao = (raw?: string): string | null => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed?.resultado_final || null;
    } catch {
        return null;
    }
};

const TermosReprovadosModal: React.FC<TermosReprovadosModalProps> = ({ negociacoes, profiles, onClose, onOpenDetails, onUpdate }) => {
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [detalhes, setDetalhes] = useState<Map<number, { analise?: any; avalistas: any[] }>>(new Map());
    // Negociações com declaração de não associação: encerradas, sem caminho de avalista.
    const [encerradas, setEncerradas] = useState<Set<number>>(new Set());
    // Cadastrar/remover avalista não muda a lista de ids do board, então o onUpdate
    // do painel sozinho não refazia esta busca: `avalistas` ficava com o valor
    // carregado na abertura do modal. Resultado prático: depois de cadastrar, o
    // painel continuava oferecendo "Adicionar avalista" e o segundo clique batia no
    // índice único idx_negociacao_avalistas_ativo ("duplicate key"), dando a
    // impressão de que o cadastro nunca finaliza.
    const [avalistasVersao, setAvalistasVersao] = useState(0);
    const { motivos: motivosReprovacao } = useMotivosReprovacao();

    const idsKey = negociacoes.map(n => n.id).join(',');

    // Busca sob demanda o que o board não traz mais: a análise completa e os avalistas.
    useEffect(() => {
        const ids = idsKey ? idsKey.split(',').map(Number) : [];
        if (ids.length === 0) { setDetalhes(new Map()); return; }

        let cancelado = false;
        const buscarAnalises = async () => {
            const comMotivo = await supabase.from('analise_cadastral').select(ANALISE_COLUNAS_DETALHE + ANALISE_COLUNAS_MOTIVO).in('negociacao_id', ids);
            if (!comMotivo.error || !/motivo/.test(comMotivo.error.message || '')) return comMotivo;
            return supabase.from('analise_cadastral').select(ANALISE_COLUNAS_DETALHE).in('negociacao_id', ids);
        };
        (async () => {
            const [analises, avalistas, comDeclaracao] = await Promise.all([
                buscarAnalises(),
                supabase.from('negociacao_avalistas').select('*').in('negociacao_id', ids),
                negociacoesComDeclaracao(ids).catch(err => {
                    console.error('Declarações do modal de termos reprovados:', err);
                    return new Set<number>();
                }),
            ]);
            if (cancelado) return;
            setEncerradas(comDeclaracao);
            if (analises.error) console.error('Análises do modal de termos reprovados:', analises.error);
            if (avalistas.error) console.error('Avalistas do modal de termos reprovados:', avalistas.error);

            const mapa = new Map<number, { analise?: any; avalistas: any[] }>();
            ids.forEach(id => mapa.set(id, { avalistas: [] }));
            (analises.data || []).forEach((a: any) => {
                const item = mapa.get(Number(a.negociacao_id));
                if (item) item.analise = a;
            });
            (avalistas.data || []).forEach((a: any) => {
                const item = mapa.get(Number(a.negociacao_id));
                if (item) item.avalistas.push(a);
            });
            setDetalhes(mapa);
        })();
        return () => { cancelado = true; };
    }, [idsKey, avalistasVersao]);

    // Mescla o detalhe por cima da negociação: o resto do componente segue lendo
    // n.analise_cadastral[0] e n.avalistas normalmente.
    const linhas = useMemo(() => negociacoes.map(n => {
        const det = detalhes.get(n.id);
        if (!det) return n;
        return {
            ...n,
            analise_cadastral: det.analise ? [det.analise] : n.analise_cadastral,
            avalistas: det.avalistas,
        } as Negociacao;
    }), [negociacoes, detalhes]);

    // Coluna de consumo e total no fim (#6427, pedido da Talita): quanto de kWh ficou para trás
    // na análise cadastral. Sem consumo preenchido mostra "—" e fica de fora da soma, com aviso.
    const totalKwh = useMemo(() => negociacoes.reduce((soma, n) => soma + consumoDe(n), 0), [negociacoes]);
    const semConsumo = useMemo(() => negociacoes.filter(n => consumoDe(n) <= 0).length, [negociacoes]);

    return (
        <>
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-5 border-b border-red-200 dark:border-red-800/40 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 shrink-0">
                    <div className="p-2.5 bg-red-100 dark:bg-red-900/40 rounded-xl">
                        <XOctagon size={20} className="text-red-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-bold text-red-900 dark:text-red-300">
                            {negociacoes.length === 1 ? 'Termo reprovado' : `${negociacoes.length} termos reprovados`}
                        </h3>
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">Clique em uma negociação para ver o motivo da reprovação.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors shrink-0"
                        title="Fechar"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Cabeçalho das colunas — mesmas larguras das linhas, para o consumo alinhar */}
                <div className="flex items-center gap-3 px-6 py-2 overflow-hidden [scrollbar-gutter:stable] border-b border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-slate-800/40 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 shrink-0">
                    <span className="flex-1">Negociação</span>
                    <span className="w-28 text-right">Consumo</span>
                    <span className="w-4 shrink-0" aria-hidden />
                </div>

                {/* List */}
                <div className="overflow-y-auto [scrollbar-gutter:stable] custom-scrollbar divide-y divide-gray-100 dark:divide-gray-800">
                    {linhas.map(n => {
                        const analise = n.analise_cadastral?.[0];
                        const analista = profiles.find(p => p.id === analise?.analista_id);
                        const isExpanded = expandedId === n.id;
                        const verificacao = parseVerificacao(analise?.verificacao_telefonica_resultado);
                        const motivo = analise?.decisao_final === 'Reprovado' ? descreverMotivosReprovacao(analise, motivosReprovacao) : null;

                        return (
                            <div key={n.id}>
                                <button
                                    onClick={() => setExpandedId(isExpanded ? null : n.id)}
                                    className="w-full text-left px-6 py-3.5 flex items-center gap-3 hover:bg-red-50/60 dark:hover:bg-red-900/10 transition-colors"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                            {n.titulo || n.cliente_nome}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                            {motivo && <><span className="font-semibold text-red-600 dark:text-red-400">{motivo.principal}</span> · </>}
                                            {formatDate(analise?.decisao_final_at || n.updated_at)}
                                            {analista?.full_name && <> · Analisado por {analista.full_name}</>}
                                        </p>
                                    </div>
                                    {analise?.decisao_final && (
                                        // No celular sai: toda linha daqui é reprovada, e o espaço vai para o nome
                                        <span className="hidden sm:inline shrink-0 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[11px] font-bold">
                                            {analise.decisao_final}
                                        </span>
                                    )}
                                    <span className={`w-28 shrink-0 text-right text-sm tabular-nums whitespace-nowrap ${consumoDe(n) > 0 ? 'font-semibold text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'}`}
                                        title={consumoDe(n) > 0 ? undefined : 'Consumo não informado na negociação'}>
                                        {consumoDe(n) > 0 ? formatKwh(consumoDe(n)) : '—'}
                                    </span>
                                    <ChevronDown size={16} className={`text-gray-300 dark:text-gray-600 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>

                                {isExpanded && (
                                    <div className="px-6 pb-5 space-y-3 animate-fade-in">
                                        {/* Etapa atual */}
                                        <div className="flex items-center gap-2">
                                            <Layers size={13} className="text-gray-400" />
                                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Etapa atual:</span>
                                            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[11px] font-semibold">
                                                {n.stage || '—'}
                                            </span>
                                        </div>

                                        {motivo && (
                                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200/60 dark:border-red-800/40">
                                                <Tag size={14} className="text-red-600 mt-0.5 shrink-0" />
                                                <p className="text-sm text-red-800 dark:text-red-300">
                                                    <strong>Motivo:</strong> {motivo.principal}
                                                    {motivo.outros.length > 0 && (
                                                        <span className="text-red-700/80 dark:text-red-300/80"> · também pesou: {motivo.outros.join(', ')}</span>
                                                    )}
                                                    {analise?.motivo_reprovacao_retroativo && (
                                                        <span className="block text-[11px] text-red-600/70 dark:text-red-400/70">Análise anterior à lista de motivos: classificado depois, a partir do parecer.</span>
                                                    )}
                                                </p>
                                            </div>
                                        )}

                                        {verificacao && VERIFICACAO_LABELS[verificacao] && (
                                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200/60 dark:border-red-800/40">
                                                <PhoneOff size={14} className="text-red-600 mt-0.5 shrink-0" />
                                                <p className="text-sm text-red-800 dark:text-red-300">{VERIFICACAO_LABELS[verificacao]}</p>
                                            </div>
                                        )}

                                        {(analise?.credito_score_serasa || analise?.credito_classificacao_risco || analise?.credito_pendencias_financeiras || analise?.credito_protestos || analise?.credito_cheques_fundo || analise?.parecer_credito) && (
                                            <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/10 border border-orange-200/60 dark:border-orange-800/40 space-y-1.5">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <ShieldAlert size={13} className="text-orange-600" />
                                                    <span className="text-xs font-bold text-orange-700 dark:text-orange-400 uppercase tracking-wider">Análise de Crédito</span>
                                                </div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-orange-900 dark:text-orange-200">
                                                    {analise?.credito_score_serasa && <span><strong>Score Serasa:</strong> {analise.credito_score_serasa}</span>}
                                                    {analise?.credito_classificacao_risco && <span><strong>Classificação de risco:</strong> {analise.credito_classificacao_risco}</span>}
                                                    {analise?.credito_pendencias_financeiras && <span><strong>Pendências financeiras:</strong> {formatMoney(analise.credito_valor_pendencias) || 'Sim'}</span>}
                                                    {analise?.credito_protestos && <span><strong>Protestos:</strong> {formatMoney(analise.credito_valor_protestos) || 'Sim'}</span>}
                                                    {analise?.credito_cheques_fundo && <span><strong>Cheques sem fundo:</strong> {formatMoney(analise.credito_valor_cheques) || 'Sim'}</span>}
                                                </div>
                                                {analise?.parecer_credito && (
                                                    <p className="text-sm text-orange-900 dark:text-orange-200 whitespace-pre-line pt-1">{analise.parecer_credito}</p>
                                                )}
                                            </div>
                                        )}

                                        {analise?.pendencias_obs_docs && (
                                            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/40">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <AlertTriangle size={13} className="text-amber-600" />
                                                    <span className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Pendências / Observações</span>
                                                </div>
                                                <p className="text-sm text-amber-900 dark:text-amber-200 whitespace-pre-line">{analise.pendencias_obs_docs}</p>
                                            </div>
                                        )}

                                        {analise?.ressalvas_condicoes && (
                                            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <MessageSquare size={13} className="text-primary" />
                                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Ressalvas / Condições</span>
                                                </div>
                                                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{analise.ressalvas_condicoes}</p>
                                            </div>
                                        )}

                                        {analise?.risco_final && analise.risco_final !== analise?.decisao_final && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400"><strong>Risco final:</strong> {analise.risco_final}</p>
                                        )}

                                        {analise?.decisao_final === 'Reprovado' && (encerradas.has(n.id) ? (
                                            <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                                                <Lock size={13} className="text-slate-500 mt-0.5 shrink-0" />
                                                <p className="text-xs text-slate-600 dark:text-slate-300">
                                                    <strong>Negociação encerrada.</strong> Já foi emitida a declaração de não associação; o avalista não está mais disponível. O PDF fica na negociação completa.
                                                </p>
                                            </div>
                                        ) : (
                                            <AvalistaRecoveryPanel
                                                negociacao={n}
                                                onUpdate={() => { setAvalistasVersao(v => v + 1); onUpdate(); }}
                                            />
                                        ))}

                                        <button
                                            onClick={() => onOpenDetails(n)}
                                            className="flex items-center gap-1.5 text-xs font-bold text-primary hover:underline pt-1"
                                        >
                                            <ExternalLink size={13} />
                                            Abrir negociação completa
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Total — fixo no fim da lista, alinhado com a coluna de consumo */}
                <div className="flex items-start gap-3 px-6 py-3 overflow-hidden [scrollbar-gutter:stable] border-t border-red-200 dark:border-red-800/40 bg-red-50/70 dark:bg-red-900/10 shrink-0">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-extrabold uppercase tracking-wider text-red-800 dark:text-red-300">Total reprovado</p>
                        <p className="text-[11px] text-red-600/80 dark:text-red-400/80">
                            {negociacoes.length === 1 ? '1 termo' : `${negociacoes.length} termos`}
                            {semConsumo > 0 && <> · {semConsumo === 1 ? '1 sem consumo informado' : `${semConsumo} sem consumo informado`}, fora da soma</>}
                        </p>
                    </div>
                    <span className="w-28 shrink-0 text-right text-base font-extrabold tabular-nums whitespace-nowrap text-red-800 dark:text-red-300">
                        {formatKwh(totalKwh)}
                    </span>
                    <span className="w-4 shrink-0" aria-hidden />
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end shrink-0 bg-gray-50/50 dark:bg-slate-900/50">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default TermosReprovadosModal;
